// Zepto Product Personalizer support (2026-08-31).
//
// A single Shopify line item carrying the `_pplr_preview` custom attribute is
// a personalized garment: its option attributes (e.g. "Select Logo" +
// "Select Club Logo") name ONE physical heat-press embellishment, and the
// `Preview` attribute holds the finished-item image. We synthesize one
// embellishment child per line item so bundle-aware surfaces (pressing, QC,
// order detail, prints) treat it like a Team Portal bundle.
//
// The synthetic group_key MUST match the backend's (database.zepto_group_key)
// — same slug + djb2 hash — because pressing's embellishment_picks and the
// picking portal's pick rows both address the same item_state row.

export const ZEPTO_MARKER_ATTR = '_pplr_preview';

function djb2hex(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
        h = (((h << 5) + h) + str.charCodeAt(i)) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}

export function isZeptoLineItem(li) {
    return (li?.customAttributes || []).some(a => a.key === ZEPTO_MARKER_ATTR);
}

// Combined embellishment title: every non-underscore attribute except the
// preview holder contributes, in attribute order. Mirrors backend
// zepto_combo_from_attrs.
export function zeptoCombo(li) {
    const attrs = li?.customAttributes || [];
    const amap = {};
    const order = [];
    for (const a of attrs) {
        const k = a?.key || '';
        if (k && !(k in amap)) { amap[k] = a?.value || ''; order.push(k); }
    }
    if (!(ZEPTO_MARKER_ATTR in amap)) return null;
    const previewKey = (amap[ZEPTO_MARKER_ATTR] || 'Preview').trim();
    const parts = [];
    for (const k of order) {
        if (k.startsWith('_') || k === 'Preview' || k === previewKey) continue;
        const v = (amap[k] || '').trim();
        if (v) parts.push(v);
    }
    return parts.length ? parts.join(' — ') : 'Personalized Logo';
}

export function zeptoGroupKey(combo) {
    const slug = (combo || '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);
    return `__zepto__${slug}-${djb2hex(combo || '')}`;
}

// Synthetic embellishment child, shaped like a line item so the shared
// bundle builders / instance-state machinery consume it unchanged:
//  - sku = the synthetic group_key (drives embPickStates + portalState reads)
//  - productType 'Logo' at BOTH accessor levels (node + variant.product)
export function zeptoChildLineItem(li) {
    const combo = zeptoCombo(li);
    if (!combo) return null;
    const key = zeptoGroupKey(combo);
    const qty = li.currentQuantity ?? li.quantity ?? 1;
    return {
        id: `${li.id}#zepto`,
        sku: key,
        title: combo,
        quantity: qty,
        currentQuantity: qty,
        productType: 'Logo',
        variant: { product: { productType: 'Logo' } },
        customAttributes: [],
        image: null,
        __zepto: true,
    };
}
